import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import {
  EnvironmentService,
  ToastService,
  SKIP_HTTP_ERROR_TOAST,
} from 'svnxii-ng-shared/services';

const ACCESS_TOKEN_KEY = 'accessToken';
const CONTEXT_TOKEN_KEY = 'contextAccessToken';
const COOKIE_AUTH_PATHS = new Set(['login', 'logout', 'refresh']);

function readCookie(cookieString: string, name: string): string | null {
  const prefix = `${name}=`;
  const cookie = cookieString.split(';').find((value) => value.trim().startsWith(prefix));

  if (!cookie) return null;

  const value = cookie.trim().slice(prefix.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isCookieAuthRequest(url: string, apiUrl: string): boolean {
  const prefix = `${apiUrl.replace(/\/$/, '')}/api/v1/iam/auth/`;
  if (!url.startsWith(prefix)) return false;

  const operation = url.slice(prefix.length).split(/[?#]/, 1)[0];
  return COOKIE_AUTH_PATHS.has(operation);
}

function resolveToken(url: string): string | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const contextToken = localStorage.getItem(CONTEXT_TOKEN_KEY);

  if (url.includes('/assignment-context-token') || url.includes('/assignment-context')) {
    return accessToken;
  }

  return contextToken ?? accessToken;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toastService = inject(ToastService);
  const translate = inject(TranslateService);
  const environment = inject(EnvironmentService);
  const document = inject(DOCUMENT);

  const usesCookieAuth = isCookieAuthRequest(req.url, environment.apiUrl);
  const token = usesCookieAuth ? null : resolveToken(req.url);
  const xsrfToken = usesCookieAuth ? readCookie(document.cookie, 'XSRF-TOKEN') : null;
  let headers = usesCookieAuth ? req.headers.delete('Authorization') : req.headers;

  if (token && !headers.has('Authorization'))
    headers = headers.set('Authorization', `Bearer ${token}`);
  if (xsrfToken) headers = headers.set('X-XSRF-TOKEN', xsrfToken);

  const authReq =
    token || usesCookieAuth
      ? req.clone({
          withCredentials: usesCookieAuth,
          headers,
        })
      : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const code = error.error?.code;

      switch (code) {
        case 'invalid_access_token':
          localStorage.clear();
          router.navigate(['/login']);
          break;

        case 'setup_required':
          router.navigate(['/organization/onboarding']);
          break;
      }

      if (error.status === 401) {
        localStorage.clear();
        router.navigate(['/login']);
      }

      if (!req.context.get(SKIP_HTTP_ERROR_TOAST))
        toastService.error(translate.instant(error.error?.detail || 'An error occurred'));

      return throwError(() => error);
    }),
  );
};
